import { addDays, daysBetween, logicalDate, type LocalDate, type TimeZone } from './time';

/**
 * CADENCIA — cuándo toca un hábito.
 *
 * Cuatro formas, y ni una más. Cada forma extra multiplica los estados de la
 * pantalla de Hoy, y la pantalla de Hoy es la que el usuario abre a diario: si
 * ahí hay que pensar, el producto ha fallado.
 *
 * La decisión que ordena todo esto: **un hábito que no toca hoy no aparece en
 * Hoy**. No sale en gris, no sale tachado, no sale «opcional». Sale la lista de
 * lo que toca, y nada más.
 */

export type Cadence =
  /** Todos los días. */
  | { readonly kind: 'daily' }
  /** Días concretos de la semana. 1 = lunes … 7 = domingo (ISO). */
  | { readonly kind: 'weekdays'; readonly days: readonly number[] }
  /** N veces por semana, el usuario elige cuáles. */
  | { readonly kind: 'timesPerWeek'; readonly times: number }
  /** N veces al mes. */
  | { readonly kind: 'timesPerMonth'; readonly times: number };

export type HabitStatus = 'done' | 'skipped' | 'pending';

export const WEEKDAY_NAMES = ['L', 'M', 'X', 'J', 'V', 'S', 'D'] as const;
export const WEEKDAY_LONG = [
  'lunes',
  'martes',
  'miércoles',
  'jueves',
  'viernes',
  'sábado',
  'domingo',
] as const;

/** Día ISO de la semana de una fecha civil: 1 = lunes … 7 = domingo. */
export function isoWeekday(localDate: LocalDate): number {
  const day = new Date(`${localDate}T00:00:00Z`).getUTCDay();
  return day === 0 ? 7 : day;
}

/** `2026-W37`. Semanas ISO: empiezan en lunes y la 1 es la del primer jueves. */
export function isoWeekKey(localDate: LocalDate): string {
  const date = new Date(`${localDate}T00:00:00Z`);
  const day = isoWeekday(localDate);
  // Al jueves de esa semana: el año ISO es el del jueves.
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const year = date.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(year, 0, 4));
  const firstDay = firstThursday.getUTCDay() || 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() + 4 - firstDay);
  const week = 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
  return `${year}-W${String(week).padStart(2, '0')}`;
}

/** `2026-09`. */
export function monthKey(localDate: LocalDate): string {
  return localDate.slice(0, 7);
}

export function weekStart(localDate: LocalDate): LocalDate {
  return addDays(localDate, -(isoWeekday(localDate) - 1));
}

export function monthStart(localDate: LocalDate): LocalDate {
  return `${localDate.slice(0, 7)}-01`;
}

/**
 * Si el hábito toca ese día.
 *
 * Los de «N veces por semana o mes» tocan **todos los días del periodo hasta
 * que se cumple la cuota**, porque la promesa es «tres veces esta semana», no
 * «los martes». Cuando la cuota está hecha, dejan de tocar y salen de Hoy: eso
 * es lo que convierte una lista en una lista que se termina.
 */
export function isDueOn(cadence: Cadence, localDate: LocalDate, doneInPeriod = 0): boolean {
  switch (cadence.kind) {
    case 'daily':
      return true;
    case 'weekdays':
      return cadence.days.includes(isoWeekday(localDate));
    case 'timesPerWeek':
    case 'timesPerMonth':
      return doneInPeriod < cadence.times;
  }
}

/** Cuántas veces toca en el periodo al que pertenece esa fecha. */
export function targetForPeriod(cadence: Cadence, localDate: LocalDate): number {
  switch (cadence.kind) {
    case 'daily':
      return 1;
    case 'weekdays':
      return cadence.days.includes(isoWeekday(localDate)) ? 1 : 0;
    case 'timesPerWeek':
    case 'timesPerMonth':
      return cadence.times;
  }
}

/** La clave del periodo de la cuota: el día, la semana o el mes. */
export function periodKeyFor(cadence: Cadence, localDate: LocalDate): string {
  switch (cadence.kind) {
    case 'daily':
    case 'weekdays':
      return localDate;
    case 'timesPerWeek':
      return isoWeekKey(localDate);
    case 'timesPerMonth':
      return monthKey(localDate);
  }
}

export function describeCadence(cadence: Cadence): string {
  switch (cadence.kind) {
    case 'daily':
      return 'todos los días';
    case 'weekdays': {
      const days = [...cadence.days].sort((a, b) => a - b);
      if (days.length === 7) return 'todos los días';
      if (days.length === 5 && days.every((d) => d <= 5)) return 'de lunes a viernes';
      if (days.length === 2 && days[0] === 6 && days[1] === 7) return 'fines de semana';
      return days.map((d) => WEEKDAY_LONG[d - 1]).join(', ');
    }
    case 'timesPerWeek':
      return cadence.times === 1 ? 'una vez por semana' : `${cadence.times} veces por semana`;
    case 'timesPerMonth':
      return cadence.times === 1 ? 'una vez al mes' : `${cadence.times} veces al mes`;
  }
}

// ---------------------------------------------------------------------------
// RACHAS SOBRE UNA CADENCIA
// ---------------------------------------------------------------------------

export type HabitDay = {
  readonly localDate: LocalDate;
  readonly status: HabitStatus;
  readonly tz: string;
};

/**
 * Racha de un hábito, respetando su cadencia.
 *
 * Tres reglas que no son obvias y que deciden si el usuario confía en el número:
 *
 *  1. **Un día en que el hábito no tocaba no rompe la racha.** Si corres lunes,
 *     miércoles y viernes, el martes no cuenta en contra.
 *  2. **Omitir a propósito no rompe la racha, pero tampoco la suma.** Es lo que
 *     separa «hoy no» de «se me olvidó». La app no castiga una decisión.
 *  3. **Un cambio de zona horaria perdona un hueco de un día** (PRD 7.4): no se
 *     puede romper una racha por un día que el usuario nunca vivió.
 *
 * LA UNIDAD DEPENDE DE LA CADENCIA. En los diarios y los de días concretos son
 * días; en los de cuota («3 veces por semana», «2 al mes») son **periodos con la
 * cuota cumplida**, uno detrás de otro. Contarlos en días era decirle a quien
 * corre lunes, miércoles y viernes que su racha es 1 porque el jueves no corrió:
 * la promesa era «tres veces esta semana», no «todos los días». Ver
 * `streakUnit` para cómo se nombra.
 */
export function habitStreak(days: readonly HabitDay[], cadence: Cadence, today: LocalDate): number {
  if (cadence.kind === 'timesPerWeek' || cadence.kind === 'timesPerMonth') {
    return quotaStreak(days, cadence, today);
  }

  const byDate = new Map<LocalDate, HabitDay>();
  for (const day of days) byDate.set(day.localDate, day);

  let streak = 0;
  let cursor = today;
  let previousTz: string | undefined;
  let misses = 0;

  // 400 días de ventana: más que cualquier racha que se vaya a enseñar, y evita
  // recorrer un historial de años en cada render.
  for (let i = 0; i < WINDOW_DAYS; i += 1) {
    const entry = byDate.get(cursor);
    const due = isDueOn(cadence, cursor);

    if (entry?.status === 'done') {
      streak += 1;
      misses = 0;
      previousTz = entry.tz;
    } else if (entry?.status === 'skipped') {
      // Ni suma ni rompe.
      misses = 0;
      previousTz = entry.tz;
    } else if (!due) {
      // No tocaba: no cuenta.
    } else if (i === 0) {
      // Hoy todavía puede hacerse: no rompe nada.
    } else {
      misses += 1;
      // El perdón del viaje: exactamente un día, y solo si la zona cambió entre
      // las dos entradas que rodean el hueco. El día del hueco no tiene entrada
      // —por eso es un hueco—, así que se mira la siguiente más antigua.
      // Un día con entrada «pendiente» sí se vivió: se abrió la app y se desmarcó.
      const older =
        previousTz === undefined || entry !== undefined ? undefined : olderEntry(byDate, cursor, i);
      const tzChanged = older !== undefined && older.tz !== previousTz;
      if (misses > 1 || !tzChanged) break;
    }

    cursor = addDays(cursor, -1);
  }

  return streak;
}

const WINDOW_DAYS = 400;

/** La entrada registrada más reciente anterior a `cursor`, dentro de la ventana. */
function olderEntry(
  byDate: ReadonlyMap<LocalDate, HabitDay>,
  cursor: LocalDate,
  walked: number,
): HabitDay | undefined {
  let probe = addDays(cursor, -1);
  for (let j = walked + 1; j < WINDOW_DAYS; j += 1) {
    const entry = byDate.get(probe);
    if (entry && entry.status !== 'pending') return entry;
    probe = addDays(probe, -1);
  }
  return undefined;
}

/**
 * Racha en periodos de una cuota: cuántas semanas (o meses) seguidos se cumplió.
 *
 *  · El periodo en curso suma si ya está cumplido y, si no, **no rompe**: la
 *    semana todavía no ha terminado, igual que «hoy» no rompe en un diario.
 *  · Un periodo pasado sin cumplir rompe, salvo que lo que falta se omitiera a
 *    propósito (hechos + omitidos ≥ cuota): entonces ni suma ni rompe, que es la
 *    regla 2 llevada al periodo.
 */
function quotaStreak(
  days: readonly HabitDay[],
  cadence: Extract<Cadence, { kind: 'timesPerWeek' | 'timesPerMonth' }>,
  today: LocalDate,
): number {
  const done = new Map<string, number>();
  const skipped = new Map<string, number>();
  const seen = new Set<LocalDate>();
  for (const day of days) {
    if (seen.has(day.localDate) || day.localDate > today) continue;
    seen.add(day.localDate);
    const key = periodKeyFor(cadence, day.localDate);
    if (day.status === 'done') done.set(key, (done.get(key) ?? 0) + 1);
    else if (day.status === 'skipped') skipped.set(key, (skipped.get(key) ?? 0) + 1);
  }

  const startOf = cadence.kind === 'timesPerWeek' ? weekStart : monthStart;
  let streak = 0;
  let periodStart = startOf(today);

  for (let i = 0; daysBetween(periodStart, today) < WINDOW_DAYS; i += 1) {
    const key = periodKeyFor(cadence, periodStart);
    const hechos = done.get(key) ?? 0;
    const omitidos = skipped.get(key) ?? 0;

    if (hechos >= cadence.times) {
      streak += 1;
    } else if (i === 0) {
      // El periodo en curso todavía puede cumplirse.
    } else if (hechos + omitidos < cadence.times) {
      break;
    }

    periodStart = startOf(addDays(periodStart, -1));
  }

  return streak;
}

/**
 * Cómo se nombra la unidad de la racha, en singular o plural según `n`:
 * «día»/«días», «semana»/«semanas», «mes»/«meses». La interfaz no puede decir
 * «días de racha» de un número que cuenta semanas.
 */
export function streakUnit(cadence: Cadence, n: number): string {
  const one = n === 1;
  switch (cadence.kind) {
    case 'daily':
    case 'weekdays':
      return one ? 'día' : 'días';
    case 'timesPerWeek':
      return one ? 'semana' : 'semanas';
    case 'timesPerMonth':
      return one ? 'mes' : 'meses';
  }
}

/** La racha más larga que hubo nunca, para el rewind y el detalle del hábito. */
export function longestStreak(days: readonly HabitDay[]): {
  length: number;
  from: LocalDate | null;
  to: LocalDate | null;
} {
  const done = days
    .filter((d) => d.status === 'done')
    .map((d) => d.localDate)
    .sort();

  let best = 0;
  let bestFrom: LocalDate | null = null;
  let bestTo: LocalDate | null = null;
  let runFrom: LocalDate | null = null;
  let run = 0;

  for (let i = 0; i < done.length; i += 1) {
    const current = done[i];
    if (current === undefined) continue;
    const previous = done[i - 1];
    if (previous !== undefined && daysBetween(previous, current) === 1) {
      run += 1;
    } else {
      run = 1;
      runFrom = current;
    }
    if (run > best) {
      best = run;
      bestFrom = runFrom;
      bestTo = current;
    }
  }

  return { length: best, from: bestFrom, to: bestTo };
}

/**
 * SI UN HÁBITO YA EXISTÍA ESE DÍA — Y SI TODAVÍA NO SE HABÍA ARCHIVADO.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * `isDueOn` contesta «¿tocaba este día?» mirando solo la cadencia, y por eso un hábito
 * diario creado hoy «tocaba» también hace tres semanas. El calendario lo enseñaba como
 * **0/1 hábitos durante veintisiete días seguidos**, es decir, veintisiete fracasos que
 * no ocurrieron porque el hábito no existía.
 *
 * Es el mismo error de familia que el del rewind diciendo «se rompió el 1 de enero»
 * cuando lo que pasó es que se acabó el año: la app inventando un fallo del usuario a
 * partir de un hueco en sus propios datos. Y en una app de hábitos eso no es un detalle
 * cosmético — enseñarle a alguien que lleva veintisiete días fallando es exactamente lo
 * que hace que cierre la app.
 *
 * La regla, entonces: **un día solo cuenta para un hábito si el hábito estaba vivo ese
 * día.** Antes de crearlo no hay nada que contar, y después de archivarlo tampoco.
 * ───────────────────────────────────────────────────────────────────────────
 */
export function estabaVigente(
  habito: { readonly createdAt?: string | null; readonly archivedAt?: string | null },
  dia: LocalDate,
  tz: TimeZone,
  dayStartMin: number,
): boolean {
  // `createdAt` y `archivedAt` son instantes; el día es una fecha flotante. Se pasa el
  // instante a SU día lógico en la zona y con el «mi día empieza a las…» del usuario.
  // Recortar los diez primeros caracteres del ISO era quedarse con la fecha UTC —el
  // bug del PRD 7.2—: un hábito creado a las 21:00 en Bogotá «nacía» al día siguiente.
  const creado = diaLogicoDe(habito.createdAt, tz, dayStartMin);
  if (creado && dia < creado) return false;

  const archivado = diaLogicoDe(habito.archivedAt, tz, dayStartMin);
  if (archivado && dia > archivado) return false;

  return true;
}

function diaLogicoDe(
  instante: string | null | undefined,
  tz: TimeZone,
  dayStartMin: number,
): LocalDate | null {
  if (!instante) return null;
  const ms = Date.parse(instante);
  // Un instante ilegible no puede excluir nada: contar de más es lo conservador.
  if (Number.isNaN(ms)) return null;
  return logicalDate(new Date(ms), tz, dayStartMin);
}
